/**
 * canvas-renderer.js
 *
 * Adapted for alice-wonder (Shopify theme, no build system).
 * Original source: abbode-theme / slow-burn
 *
 * Contains:
 * - Global redraw/canvas setup (lines 4170-4173)
 * - DOMContentLoaded canvas block (lines 4175-4830):
 *     focal point handling, retina canvas setup, iconAspectRatioCache,
 *     getIconDrawDimensions(), getIconYShift(), window.redrawCanvas(),
 *     drawCustomizations() (icon drawing, text drawing, layout group
 *     position overrides, design-mode bounding boxes),
 *     updateThumbnail(), store subscription, zoom toggle
 * - Global canvas sizing + resize handler (lines 4837-4881):
 *     baseIconSize, baseTextSize, mobile scaling,
 *     window.iconSize, window.textSize
 *
 * ============================================================================
 * DEPENDENCIES / PREREQUISITES
 * ============================================================================
 *
 * Requires: Pinia customizerStore via window.useCustomizerStore()
 *   - window.Pinia, window.useCustomizerStore, and window.customizerStore
 *     must be available as globals before this file runs (no ES module imports;
 *     the store is accessed inside the DOMContentLoaded handler).
 *   - customizerStore.customizations is the reactive object whose keys are
 *     property names and whose values describe each customization:
 *       { type: 'icon'|'text'|'font'|...,
 *         x, y,                              // position on canvas (px)
 *         size,                              // draw size in px
 *         url,                               // (icon) image URL
 *         rotation,                          // degrees
 *         color,                             // (text) hex fill color
 *         font,                              // (text) CSS class name
 *         value,                             // (text) string to render
 *         layoutGroup: {                     // (text) optional group config
 *           direction, gap, linkedIconProperty, iconSize
 *         },
 *         multilineConfig: { enabled, maxCharsPerLine },
 *         alignment,                         // 'top'|'center'|'bottom'
 *         borderColor,                       // (icon) debug swatch color
 *         minSize, maxSize, defaultSize,     // (icon) handwriting size bounds
 *         enableSizeScaling,                 // (icon) whether scaling is on
 *         appliesToProperty,                 // (font) which text prop it styles
 *         fontScale, lineHeight              // (font) multipliers
 *       }
 *
 * Requires DOM elements (must exist in the page HTML):
 *   - #customizer-canvas          <canvas> element where compositing happens
 *   - .base-image                 <img> showing the current product variant photo
 *   - #customizer-base-images     <script type="application/json"> containing a
 *                                 JSON map { variantId: { url, focal_point } }
 *   - .masterSelect               <select> with the currently-selected variant
 *   - #canvas-settings            element with data-icon-size / data-text-size
 *   - .zoom-toggle                <button> to toggle the .zoomed CSS class
 *   - .customiser-toggle          element whose .active class signals the
 *                                 customizer UI is open
 *   - .thumbnails .preview-base img   thumbnail <img> updated from canvas data
 *
 * Requires CSS classes:
 *   - .zoomed      applied to canvas when user zooms in
 *   - .hidden      if present on canvas, redraws are skipped
 *   - .active      on .customiser-toggle means the customizer is visible
 *
 * Window globals SET by this file (consumed by other modules):
 *   - window.redrawCanvas         function – call to repaint the canvas
 *   - window.canvasBaseImage      Image – the current product variant photo
 *   - window.currentFocalPoint    string – CSS transform-origin for zoom
 *   - window.iconSize             number – current icon draw size (px)
 *   - window.textSize             number – current text draw size (px)
 *
 * ============================================================================
 * ARCHITECTURE OVERVIEW
 * ============================================================================
 *
 * 1. The Pinia customizerStore holds all customization state (text values,
 *    icon URLs, positions, colors, fonts, etc.).
 * 2. This file subscribes to the store and calls redrawCanvas() whenever the
 *    store mutates, provided the customizer UI is visible.
 * 3. redrawCanvas() clears the <canvas>, draws the base product image, then
 *    iterates every customization to composite icons and text on top.
 * 4. Layout groups allow a text and its linked icon to be positioned as a
 *    unit (above/below/left/right with a configurable gap).
 * 5. A debounced thumbnail updater copies the canvas to a product thumbnail
 *    so the gallery reflects live customization.
 * 6. Retina/HiDPI: the canvas backing store is scaled by devicePixelRatio
 *    while CSS dimensions stay at 1x, giving crisp rendering on Retina screens.
 * 7. Font rendering uses a two-pass strategy: draw immediately with whatever
 *    font is available (may be a fallback), then re-draw after
 *    document.fonts.load() confirms the web font is ready.
 * ============================================================================
 */

// ---------------------------------------------------------------------------
// GLOBAL SENTINELS
// These are set to real values inside the DOMContentLoaded handler below.
// Other modules (e.g. the variant-switcher or the customizer UI) read
// window.redrawCanvas to trigger a repaint, and window.canvasBaseImage to
// swap the product photo when the selected variant changes.
// ---------------------------------------------------------------------------
window.redrawCanvas = null;
window.canvasBaseImage = null;
window.currentFocalPoint = null;
window.customizerLocations = [];      // location configs from metaobjects
window.customizerVariantImages = {};  // { variantId: { images: [...] } }
window.currentLocationIndex = -1;     // which location is active (-1 = none selected, shows overview)
window.currentViewName = null;        // which view (side) is active (e.g. "Right Side")
window.currentSpotIndex = 0;          // which spot within the view is active
window.setCustomizerLocation = null;  // function(index) – switch location & redraw

// ===========================================================================
// MAIN INITIALIZATION – runs once the DOM is ready.
// Everything inside this handler is scoped to the page lifetime.
// ===========================================================================
document.addEventListener('DOMContentLoaded', function () {
  const customizerCanvas = document.getElementById('customizer-canvas');
  const imageBase = document.querySelector('.base-image');
  let customizerStore = null;

  // Guard: if the page has no customizer canvas or base product image,
  // this is not a customizable product page – bail out silently.
  if (!customizerCanvas || !imageBase) {
    return;
  }

  // -------------------------------------------------------------------------
  // FOCAL POINT HANDLING
  // Each product variant can define a focal_point (e.g. "40% 60%") that
  // controls the CSS transform-origin when the canvas is zoomed in.
  // This keeps the interesting part of the product photo visible during zoom.
  // On mobile (<=767px) a different default is used (top-quarter) because
  // the viewport is narrower.
  // -------------------------------------------------------------------------
  function applyFocalPointToCanvas(focalPoint) {
    const canvas = document.getElementById('customizer-canvas');
    if (!canvas) return;

    // Parse and apply focal point, default to center if no focal point
    let transformOrigin = 'center';
    if (focalPoint && focalPoint !== 'null' && focalPoint.trim() !== '') {
      transformOrigin = focalPoint;
    }


    // Apply different transform origins for desktop and mobile
    if (window.innerWidth <= 767) {
      // On mobile, use the focal point or default to 50% 25%
      canvas.style.setProperty('--mobile-transform-origin', transformOrigin === 'center' ? '50% 25%' : transformOrigin);
    } else {
      // On desktop, use the focal point or default to center
      canvas.style.setProperty('--desktop-transform-origin', transformOrigin);
    }

    // Apply the transform origin directly to the canvas
    canvas.style.transformOrigin =
      transformOrigin === 'center' && window.innerWidth <= 767 ? '50% 25%' : transformOrigin;
  }

  // Read the per-variant base-image JSON embedded in the page by Liquid.
  // New structure: { locations: [...], variants: { id: { images: [...] } } }
  // Legacy structure (fallback): { "variantId": "url" }
  let customizerBaseImagesRaw = {};
  try {
    const _rawText = document.querySelector('#customizer-base-images')?.textContent || '{}';
    console.log('canvas-renderer: raw JSON:', _rawText);
    customizerBaseImagesRaw = JSON.parse(_rawText);
    console.log('canvas-renderer: parsed OK', customizerBaseImagesRaw);
    if (customizerBaseImagesRaw.locations) {
      customizerBaseImagesRaw.locations.forEach(function(loc, i) {
        console.log('Location[' + i + ']:', loc.label, 'x:', loc.x, 'y:', loc.y, 'rotation:', loc.text_rotation);
      });
    }
  } catch (e) {
    console.error('canvas-renderer: JSON parse error in #customizer-base-images:', e.message);
    const _rawText = document.querySelector('#customizer-base-images')?.textContent || '';
    console.error('canvas-renderer: FULL raw JSON:', _rawText);
    const pos = parseInt((e.message.match(/position (\d+)/) || [])[1]) || 0;
    console.error('JSON near error (pos ' + pos + '):', JSON.stringify(_rawText.substring(Math.max(0, pos - 80), pos + 80)));
  }

  // Normalise: support both new multi-location format and old single-image format
  let customizerLocations = [];
  let customizerVariantImages = {};

  // Primary image font/icon sizes from config (used when rendering all locations on image 0)
  let primaryFontSize = customizerBaseImagesRaw.font_size_primary || 0;
  let primaryIconSize = customizerBaseImagesRaw.icon_size_primary || 0;

  if (customizerBaseImagesRaw.locations && customizerBaseImagesRaw.variants) {
    // New format
    customizerLocations = customizerBaseImagesRaw.locations;
    customizerVariantImages = customizerBaseImagesRaw.variants;
  } else {
    // Legacy format: { "variantId": "url" } — wrap each in the new shape
    customizerLocations = [{ label: 'Default', sort_order: 1, x: 50, y: 50, text_rotation: 0 }];
    for (const [vid, url] of Object.entries(customizerBaseImagesRaw)) {
      if (typeof url === 'string') {
        customizerVariantImages[vid] = { images: [url] };
      } else if (url && url.images) {
        customizerVariantImages[vid] = url;
      }
    }
  }

  // Expose globally so other modules (location tabs, integration.js) can read them
  window.customizerLocations = customizerLocations;
  window.customizerVariantImages = customizerVariantImages;
  window.customizerDefaultImageIndex = customizerBaseImagesRaw.default_image_index || 0;
  window.currentLocationIndex = -1; // No location selected initially — shows overview image

  // Determine which variant is selected right now (the <select> is rendered
  // server-side with the [selected] attribute on the initial option).
  const currentVariantId = document.querySelector('.masterSelect option[selected]')?.value;

  /**
   * Get the base image URL for a given variant.
   * All locations share the same product image — the canvas zooms into
   * each location's bounds area.
   */
  function getBaseImageUrl(variantId, locationIndex) {
    var variantData = customizerVariantImages[variantId];
    if (!variantData || !variantData.images) {
      // Fallback: try first variant that has images
      for (const vd of Object.values(customizerVariantImages)) {
        if (vd.images && vd.images[0]) return vd.images[0];
      }
      return null;
    }

    // Overview mode (no location selected, locationIndex === -1 or undefined):
    // use the default_image_index from config, falling back to image 0.
    if (locationIndex === undefined || locationIndex === null || locationIndex < 0) {
      var defIdx = window.customizerDefaultImageIndex || 0;
      return variantData.images[defIdx] || variantData.images[0] || null;
    }

    var imageIndex = locationIndex + 1;
    var loc = customizerLocations[locationIndex];
    // Explicit image_index override from metaobject (1-based for merchants, subtract 1)
    if (loc && loc.image_index != null) {
      imageIndex = loc.image_index - 1;
    } else if (loc && loc.viewIndex !== undefined) {
      // Use the location's viewIndex if defined (offset by +1)
      imageIndex = loc.viewIndex + 1;
    }

    if (variantData.images[imageIndex]) return variantData.images[imageIndex];
    // Fallback to overview image
    if (variantData.images[0]) return variantData.images[0];
    return null;
  }

  /**
   * Get the current location's position (percentage-based).
   */
  function getCurrentLocationPosition() {
    const loc = customizerLocations[window.currentLocationIndex];
    if (loc) return { x: loc.x || 50, y: loc.y || 50, rotation: loc.text_rotation || 0 };
    return { x: 50, y: 50, rotation: 0 };
  }

  // Expose position getter globally so blocks/integration can read it
  window.getCustomizerLocationPosition = getCurrentLocationPosition;

  /**
   * Switch the active location (e.g. from "Front" to "Back").
   * Updates the base image and redraws the canvas.
   */
  window.setCustomizerLocation = function (locationIndex) {
    if (locationIndex < -1 || locationIndex >= customizerLocations.length) return;
    window.currentLocationIndex = locationIndex;

    // -1 = primary image / overview mode (all locations render with primary coords)
    if (locationIndex >= 0) {
      // Track view/spot state for two-level UI
      var loc = customizerLocations[locationIndex];
      if (loc) {
        window.currentViewName = loc.view || loc.label;
        window.currentSpotIndex = 0;
        for (var i = 0; i < locationIndex; i++) {
          if ((customizerLocations[i].view || customizerLocations[i].label) === window.currentViewName) {
            window.currentSpotIndex++;
          }
        }
      }
    }

    const selectedVariantId = document.querySelector('.masterSelect option[selected]')?.value || currentVariantId;
    const newUrl = getBaseImageUrl(selectedVariantId, locationIndex);

    if (newUrl && window.canvasBaseImage) {
      // Check if the URL actually changed (compare by pathname to handle
      // relative vs absolute URL mismatches)
      const currentSrc = window.canvasBaseImage.src || '';
      const isSameImage = currentSrc.indexOf(newUrl) !== -1 || newUrl.indexOf(currentSrc) !== -1 || currentSrc === newUrl;

      if (!isSameImage) {
        // Different image — load it, redraw when ready
        window.canvasBaseImage.onload = () => {
          if (typeof window.redrawCanvas === 'function') window.redrawCanvas();
        };
        window.canvasBaseImage.src = newUrl;
        return; // wait for onload
      }
    }
    // Same image or no image change — just redraw with new zoom/location
    if (typeof window.redrawCanvas === 'function') window.redrawCanvas();
  };

  /**
   * Switch the base image when the variant changes (called by global.js / integration).
   * Keeps the current location index and loads the new variant's image for that location.
   */
  window.setCustomizerVariant = function (variantId) {
    const newUrl = getBaseImageUrl(variantId, window.currentLocationIndex);
    if (newUrl && window.canvasBaseImage) {
      window.canvasBaseImage.src = newUrl;
    }
    if (typeof window.redrawCanvas === 'function') {
      window.redrawCanvas();
    }
  };

  // Focal point: not yet per-location (can be added to metaobject later).
  // For now apply a sensible default.
  applyFocalPointToCanvas('center');

  // Re-apply focal point on resize because mobile vs desktop use different
  // default origins ("50% 25%" vs "center").
  window.addEventListener('resize', function () {
    if (window.currentFocalPoint) {
      applyFocalPointToCanvas(window.currentFocalPoint);
    }
  });

  // -------------------------------------------------------------------------
  // BASE PRODUCT IMAGE
  // The canvas composites everything on top of this product photo.
  // crossOrigin = 'anonymous' is required so canvas.toDataURL() works
  // (avoids tainting the canvas with a cross-origin image).
  // Other modules swap window.canvasBaseImage.src when the variant changes.
  // -------------------------------------------------------------------------
  window.canvasBaseImage = new Image();
  window.canvasBaseImage.crossOrigin = 'anonymous';
  // Use the multi-location-aware URL lookup, falling back to the DOM base image
  const initialBaseUrl = getBaseImageUrl(currentVariantId) || imageBase.src;
  window.canvasBaseImage.src = initialBaseUrl;

  // -------------------------------------------------------------------------
  // RETINA / HiDPI CANVAS SCALING
  //
  // Strategy: multiply the canvas backing-store resolution by devicePixelRatio
  // (e.g. 2x on a Retina display) while keeping the CSS size at 1x.
  // Then scale the 2D context by dpr so all subsequent draw calls use
  // logical (CSS) coordinates — the browser renders at the higher resolution.
  //
  // NOTE: customizerCanvas.width/height are the *HTML attribute* values
  // (set in pixels in the Liquid template). We read them before overwriting.
  // The commented-out style.width/height lines suggest CSS sizing is handled
  // elsewhere (e.g. via Tailwind or the theme stylesheet).
  // -------------------------------------------------------------------------
  const dpr = window.devicePixelRatio || 1;
  const zoomFactor = 3; // extra resolution for CSS zoom (up to 2.5x)
  const renderScale = dpr * zoomFactor;
  const cssWidth = customizerCanvas.width;   // logical width  (before scaling)
  const cssHeight = customizerCanvas.height; // logical height (before scaling)
  customizerCanvas.width = cssWidth * renderScale;   // physical backing-store width
  customizerCanvas.height = cssHeight * renderScale; // physical backing-store height
  const customizerCanvasCtx = customizerCanvas.getContext('2d');
  customizerCanvasCtx.setTransform(1, 0, 0, 1, 0, 0); // reset any leftover transforms
  customizerCanvasCtx.scale(renderScale, renderScale); // all future draws use CSS-px coords
  // NOTE: width stays PINNED at cssWidth (700) for every product so that a
  // config's absolute font_size / icon_size (px on a 700-wide canvas) keep the
  // same on-screen scale across products. Only the HEIGHT tracks the image —
  // see resizeCanvasToImage() below. These are `let` (not `const`) because the
  // height is recomputed whenever the base image (variant/location) changes.
  let canvasWidth = cssWidth;    // logical width  — pinned at 700
  let canvasHeight = cssHeight;  // logical height — recomputed from image aspect

  // -------------------------------------------------------------------------
  // resizeCanvasToImage()  — SHOP-160 bandana fix
  //
  // Problem: the canvas was locked to a 3:4 box (700×933) regardless of the
  // product photo's real shape. For a 3:4 product that lines up, but for a
  // square/wide bandana the canvas box is TALLER than the displayed photo.
  // Positions are stored as a % of the image and mapped onto the canvas via
  // pctToPxY(), so a point at y≈80% landed in the dead space *below* the
  // bandana → text/icons (especially the lower element of a combined group)
  // hung off the bottom edge.
  //
  // Fix: keep the logical width at 700 and set the logical height to
  // 700 / imageAspectRatio so the canvas box is the SAME shape as the photo.
  // Then pctToPxY() maps onto the actual image by construction — no offsets,
  // no per-product nudging. 3:4 products are unchanged (height stays ~933).
  //
  // Must run on EVERY base-image load (first load, variant swap, location
  // swap), which is why redrawCanvas() calls it up-front. Guarded so it only
  // touches the (expensive) retina backing store when the ratio actually
  // changes — avoids per-redraw flicker.
  // -------------------------------------------------------------------------
  function resizeCanvasToImage() {
    const img = window.canvasBaseImage;
    if (!img || !img.complete || img.naturalWidth === 0) return;

    const ratio = img.naturalWidth / img.naturalHeight;
    const newHeight = Math.round(canvasWidth / ratio);
    if (newHeight === canvasHeight) return; // ratio unchanged — nothing to do

    canvasHeight = newHeight;

    // Re-size the retina backing store for the new logical height. Writing
    // canvas.width/height resets the 2D context, so we must re-apply the
    // renderScale transform afterwards or every subsequent draw is mis-scaled.
    customizerCanvas.width = canvasWidth * renderScale;
    customizerCanvas.height = canvasHeight * renderScale;
    customizerCanvasCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);

    // Keep the element's CSS box in sync so the overlay bitmap and the base
    // <img> underneath occupy the exact same rect (the inline `aspect-ratio`
    // in the Liquid markup is removed so this value wins).
    customizerCanvas.style.aspectRatio = canvasWidth + ' / ' + canvasHeight;
  }

  // Draw the base image as cover-fit (center-crop) so the 700×933 canvas
  // is always fully filled.  Mismatched aspect ratios are cropped from center.
  function drawBaseImage() {
    const img = window.canvasBaseImage;
    if (!img || !img.complete || img.naturalWidth === 0) return;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const canvasRatio = canvasWidth / canvasHeight;
    let sx, sy, sw, sh;
    if (imgRatio > canvasRatio) {
      // Image is wider — crop sides
      sh = img.naturalHeight;
      sw = img.naturalHeight * canvasRatio;
      sx = (img.naturalWidth - sw) / 2;
      sy = 0;
    } else {
      // Image is taller — crop top/bottom
      sw = img.naturalWidth;
      sh = img.naturalWidth / canvasRatio;
      sx = 0;
      sy = (img.naturalHeight - sh) / 2;
    }
    customizerCanvasCtx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasWidth, canvasHeight);
  }

  // The <img> element displays the product photo.
  // canvasBaseImage is kept for thumbnail generation only.

  // -------------------------------------------------------------------------
  // ICON DIMENSION CACHES
  //
  // iconAspectRatioCache (Map<url, number>):
  //   Stores naturalWidth/naturalHeight for every icon URL that has been
  //   loaded. This lets the layout-group position calculation know the real
  //   aspect ratio of each icon *before* the image loads on the next redraw.
  //   Without this cache, the first render would assume a 1:1 square icon
  //   and the text/icon pair would jump when the image finally arrives.
  //
  // iconYShiftCache (Map<url, number>):
  //   Stores the centre-of-mass Y offset for each icon. Used by
  //   getIconYShift() to vertically nudge icons whose visual weight is not
  //   centered (e.g. a logo with a tall top section and small bottom text).
  // -------------------------------------------------------------------------
  const iconYShiftCache = new Map();
  const iconAspectRatioCache = new Map();

  /**
   * Given a square bounding-box `size` and the image's aspect ratio,
   * return the actual drawWidth and drawHeight that maintain proportions
   * while fitting inside the bounding box.
   *   aspectRatio > 1 => landscape => full width, reduced height
   *   aspectRatio < 1 => portrait  => reduced width, full height
   *   aspectRatio = 1 => square    => size x size
   */
  function getIconDrawDimensions(size, aspectRatio) {
    if (!aspectRatio || aspectRatio === 1) return { drawWidth: size, drawHeight: size };
    if (aspectRatio > 1) return { drawWidth: size, drawHeight: size / aspectRatio };
    return { drawWidth: size * aspectRatio, drawHeight: size };
  }

  // -------------------------------------------------------------------------
  // ICON Y-SHIFT (Centre-of-Mass Calculation)
  //
  // Problem: many icon images (logos, crests) have their visual weight biased
  // towards the top or bottom. If we center them geometrically, they *look*
  // off-center. This function computes a vertical correction factor.
  //
  // Method:
  //   1. Draw the icon onto a small offscreen canvas (max 200px) for speed.
  //   2. Scan every pixel; ignore transparent pixels and (optionally) white
  //      background pixels.
  //   3. Compute the alpha-weighted average Y position (centre of mass).
  //   4. Return the difference from the geometric centre as a fraction of
  //      the image height. Positive = content is above centre, shift down.
  //
  // The result is cached per URL so subsequent redraws are instant.
  // -------------------------------------------------------------------------
  /**
   * Compute how far down the icon should be nudged so its visual centre-of-mass
   * sits at the geometric centre of the draw area.  Works by scanning pixel
   * alpha on a tiny offscreen canvas and finding the weighted-average Y of
   * all opaque content.  Returns a ratio (0 = no shift, positive = shift down).
   */
  function getIconYShift(img) {
    const url = img.src;
    if (iconYShiftCache.has(url)) return iconYShiftCache.get(url);

    try {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) { iconYShiftCache.set(url, 0); return 0; }

      const maxDim = 200;
      const scale = Math.min(1, maxDim / Math.max(w, h));
      const sw = Math.round(w * scale);
      const sh = Math.round(h * scale);

      const offscreen = document.createElement('canvas');
      offscreen.width = sw;
      offscreen.height = sh;
      const ctx = offscreen.getContext('2d');
      ctx.drawImage(img, 0, 0, sw, sh);

      const data = ctx.getImageData(0, 0, sw, sh).data;

      // Detect whether the image has a white background (as opposed to
      // transparent). We sample the bottom-right corner pixel; if it is
      // opaque white, we treat all white pixels as "background" and skip
      // them during the centre-of-mass scan.
      const ci = ((sh - 1) * sw + (sw - 1)) * 4;
      const bgWhite = data[ci + 3] > 200 && data[ci] > 250 && data[ci + 1] > 250 && data[ci + 2] > 250;

      // Weighted Y centre-of-mass
      let totalWeight = 0;
      let weightedY = 0;
      for (let row = 0; row < sh; row++) {
        let rowWeight = 0;
        for (let col = 0; col < sw; col++) {
          const idx = (row * sw + col) * 4;
          const a = data[idx + 3];
          if (a < 30) continue; // transparent
          if (bgWhite && a > 200 && data[idx] > 250 && data[idx + 1] > 250 && data[idx + 2] > 250) continue;
          rowWeight += a; // weight by alpha so faint pixels count less
        }
        totalWeight += rowWeight;
        weightedY += rowWeight * row;
      }

      if (totalWeight === 0) { iconYShiftCache.set(url, 0); return 0; }

      const visualCenterY = weightedY / totalWeight;          // px from top
      const geometricCenterY = (sh - 1) / 2;
      // Positive shift = content is above centre → need to move image down
      const shift = (geometricCenterY - visualCenterY) / sh;  // as fraction of image height

      iconYShiftCache.set(url, shift);
      return shift;
    } catch (e) {
      iconYShiftCache.set(url, 0);
      return 0;
    }
  }

  // -------------------------------------------------------------------------
  // REDRAW VERSION PATTERN (stale-load cancellation)
  //
  // Because icon images and web fonts load asynchronously, their onload
  // callbacks can fire *after* a newer redraw has already started.  Without
  // protection, the stale callback would paint over the newer frame.
  //
  // Solution: each call to redrawCanvas() increments `redrawVersion` and
  // captures the current value.  Every async callback checks its captured
  // version against the live `redrawVersion`; if they differ, the callback
  // is stale and returns immediately without drawing.
  // -------------------------------------------------------------------------
  let redrawVersion = 0;

  /**
   * Master redraw entry point.  Clears the canvas, repaints the base product
   * image, then draws every customization from the Pinia store on top.
   * Called by: store subscription, resize handler, variant switcher, etc.
   * Exposed globally so other modules can trigger a repaint.
   */
  // -------------------------------------------------------------------------
  // SPOT POSITION INDICATORS
  //
  // Convert percentage coordinates (0-100) to canvas pixels
  function pctToPxX(pct) { return (pct / 100) * canvasWidth; }
  function pctToPxY(pct) { return (pct / 100) * canvasHeight; }

  window.redrawCanvas = function () {
    // Increment version to invalidate any in-flight async image/font loads
    redrawVersion++;

    // Match the canvas box to the current product image's aspect ratio BEFORE
    // drawing, so pctToPxY() lands on the actual photo and not a forced 3:4
    // box (SHOP-160). No-op when the image ratio hasn't changed.
    resizeCanvasToImage();

    // Reset transform to renderScale baseline, then clear
    customizerCanvasCtx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
    customizerCanvasCtx.clearRect(0, 0, canvasWidth, canvasHeight);

    // The <img> element handles the product photo display.
    // The canvas only draws customisation overlays (text, icons).
    drawCustomizations();
  };

  // =========================================================================
  // drawCustomizations()
  //
  // Iterates the customizerStore.customizations map and paints each
  // customization (icon or text) onto the canvas.  Two phases:
  //   Phase A – Compute layout-group position overrides.
  //   Phase B – Iterate all customizations and draw icons / text.
  //
  // This is called at the end of redrawCanvas() (after the base image is
  // drawn) and also by the deferred base-image-onload path.
  // =========================================================================
  function drawCustomizations() {
    if (!customizerStore) return;
    const customizations = customizerStore.customizations;

    // Primary image mode: when currentLocationIndex === -1, render ALL locations
    // using their primary image coordinates (x_primary, y_primary, etc.)
    const isPrimaryMode = window.currentLocationIndex === -1;

    // Shared-image mode: locations that share the same explicit image_index
    // (e.g. tote panels all set to image_index=1) render together even when
    // only one is selected.  Locations with a different image_index (e.g. a
    // strap close-up) are filtered out normally.
    const currentLocConfig = !isPrimaryMode && window.currentLocationIndex >= 0
      ? customizerLocations[window.currentLocationIndex] : null;
    const currentImgIdx = currentLocConfig ? currentLocConfig.image_index : null;
    function sharesCurrentImage(locIdx) {
      if (currentImgIdx == null) return false;
      const loc = customizerLocations[locIdx];
      return loc && loc.image_index != null && loc.image_index === currentImgIdx;
    }

    // Helper: get primary image overrides for a location index
    function getPrimaryOverrides(locIdx) {
      var loc = customizerLocations[locIdx];
      if (!loc) return null;
      return {
        x: pctToPxX(loc.x_primary !== undefined ? loc.x_primary : loc.x || 50),
        y: pctToPxY(loc.y_primary !== undefined ? loc.y_primary : loc.y || 50),
        rotation: loc.rotation_primary !== undefined ? loc.rotation_primary : loc.text_rotation || 0,
        fontSize: primaryFontSize,
        iconSize: primaryIconSize
      };
    }

    // -----------------------------------------------------------------------
    // PHASE A: LAYOUT GROUP POSITION OVERRIDES
    //
    // A "layout group" links a text customization to an icon customization
    // so they move as a unit.  The store stores x/y as the *group centre*;
    // this loop computes the *individual* positions for the text and its
    // linked icon, offset from that centre by direction + gap.
    //
    // Directions:
    //   "below" – icon below text    "above" – icon above text
    //   "right" – icon to the right  "left"  – icon to the left
    //
    // The computed overrides are stored in `positionOverrides[propName]`
    // and consumed during the drawing loop in Phase B.
    // -----------------------------------------------------------------------
    const positionOverrides = {};
    for (const pName in customizations) {
      const c = customizations[pName];
      // Location filter: in primary mode draw all, otherwise only active location
      const pLocSuffix = pName.match(/_(\d+)$/);
      const pLocIdxFilter = pLocSuffix ? parseInt(pLocSuffix[1], 10) : 0;
      if (pLocSuffix && !isPrimaryMode && pLocIdxFilter !== window.currentLocationIndex && !sharesCurrentImage(pLocIdxFilter)) continue;
      // Only text customizations define a layoutGroup; skip everything else.
      if (c.type !== 'text' || !c.layoutGroup) continue;

      const { direction, gap, linkedIconProperty, iconSize, expansionDirection } = c.layoutGroup;
      const pLocIdx = pLocSuffix ? parseInt(pLocSuffix[1], 10) : 0;
      const pOverrides = isPrimaryMode ? getPrimaryOverrides(pLocIdx) : null;

      // In primary mode, use the smaller primary font/icon sizes so the
      // layout group gap scales proportionally to the overview image.
      const baseFontSize = (pOverrides && pOverrides.fontSize) ? pOverrides.fontSize : (c.size || 16);
      const effectiveIconSize = (pOverrides && pOverrides.iconSize) ? pOverrides.iconSize : iconSize;

      // Look up the linked icon's *cached* aspect ratio so we can compute
      // accurate draw dimensions without waiting for the image to load.
      // On the very first render the cache may be empty (cachedAR = null);
      // in that case getIconDrawDimensions falls back to a 1:1 square.
      // When the icon's onload fires later, it populates the cache and
      // triggers a full redrawCanvas() to fix the positions.
      const linkedIcon = customizations[linkedIconProperty];
      const cachedAR = linkedIcon?.url ? (iconAspectRatioCache.get(linkedIcon.url) ?? null) : null;
      const { drawWidth: iconDrawWidth, drawHeight: iconDrawHeight } = getIconDrawDimensions(effectiveIconSize, cachedAR);

      // Find the font customization that styles this text property.
      // Font customizations are stored under keys like "font-<textPropName>"
      // and declare fontScale (multiplier) and lineHeight (multiplier).
      let fontScale = 1;
      let fontLH = 1.1;
      let fontClass = c.font || 'font-1';
      let fontUrl = null;
      let fontFamily = null;
      const basePName = pName.replace(/_\d+$/, '');
      for (const key in customizations) {
        const fc = customizations[key];
        if (fc.type === 'font' &&
            (fc.appliesToProperty === pName ||
             fc.appliesToProperty === basePName ||
             (key.includes('font-') && key.replace('font-', '') === pName))) {
          fontScale = fc.fontScale ?? 1;
          fontLH = fc.lineHeight ?? 1.1;
          if (fc.value) fontClass = fc.value;
          if (fc.fontUrl) fontUrl = fc.fontUrl;
          if (fc.fontFamily) fontFamily = fc.fontFamily;
          break;
        }
      }

      const fontSize = baseFontSize * fontScale;
      const lineHeight = fontSize * fontLH;

      // Detect second line: either value already contains a literal '\n',
      // or the text length exceeds maxCharsPerLine (handles the timing gap
      // where the auto-wrap logic in the store hasn't inserted '\n' yet).
      const maxCharsPerLine = c.multilineConfig?.maxCharsPerLine || 12;
      const hasSecondLine = c.multilineConfig?.enabled && c.value &&
        (c.value.includes('\n') || c.value.length > maxCharsPerLine);

      // "Estimated text height" (eth): the vertical space the text block
      // occupies.  The 2.2x multiplier (visualLineHeight) accounts for
      // decorative / script fonts whose ascenders, descenders and flourishes
      // extend well beyond the em-box.  For standard fonts this overshoots
      // slightly, which just adds a bit of extra gap — acceptable.
      // Used by legacy center-anchor layout groups.
      const visualLineHeight = fontSize * 2.2;
      const eth = hasSecondLine ? lineHeight + visualLineHeight : visualLineHeight;
      // Measure actual text height via canvas measureText() for accurate
      // layout-group spacing.  With textBaseline='middle', ascent+descent
      // gives the full visual height of the text (including script-font
      // flourishes).  Falls back to fontSize * 2.0 if the font hasn't loaded
      // yet or measureText isn't supported.
      // Simple text height: fontSize is a reliable proxy for center-to-edge distance
      let singleLineTextHeight = fontSize;
      const spacingTh = hasSecondLine ? lineHeight + singleLineTextHeight : singleLineTextHeight;

      // gx/gy = the "group centre" — stored as percentages, convert to px.
      // In primary mode, use the primary image coordinates instead.
      const gx = pOverrides ? pOverrides.x : pctToPxX(c.x);
      const gy = pOverrides ? pOverrides.y : pctToPxY(c.y);

      // In primary mode, scale the gap proportionally to the font size ratio
      // so spacing looks correct on the smaller overview image.
      const effectiveGap = pOverrides ? gap * (baseFontSize / (c.size || 16)) : gap;

      // Group rotation: offsets must follow the location's rotation axis
      // so the icon appears "below" along the rotated angle, not straight down.
      const groupRotation = (pOverrides && pOverrides.rotation !== undefined) ? pOverrides.rotation : (c.rotation || 0);
      const rad = groupRotation * Math.PI / 180;
      const cosR = Math.cos(rad);
      const sinR = Math.sin(rad);
      // Rotate an offset (dx, dy) around the anchor point
      function rotateOffset(dx, dy) {
        return { x: dx * cosR - dy * sinR, y: dx * sinR + dy * cosR };
      }

      // Expansion direction controls which edge stays anchored at gx/gy.
      // When undefined (legacy layout groups), fall through to center-anchor.
      // Stacking direction controls icon position relative to text.
      console.log('[Phase A] layoutGroup for', pName, '→ direction:', direction, 'expansion:', expansionDirection, 'gap:', gap, 'iconSize:', iconSize, 'linkedIcon:', linkedIconProperty, 'eth:', eth, 'spacingTh:', spacingTh, 'iconDraw:', iconDrawWidth, 'x', iconDrawHeight, 'gx:', gx, 'gy:', gy);
      // Helper: apply rotated offset from anchor point (gx, gy)
      function offsetPos(dx, dy) {
        var r = rotateOffset(dx, dy);
        return { x: gx + r.x, y: gy + r.y };
      }

      switch (direction) {
        // "below" = icon sits BELOW the text
        case 'below': {
          if (expansionDirection === 'bottom') {
            positionOverrides[pName] = { x: gx, y: gy };
            positionOverrides[linkedIconProperty] = offsetPos(0, spacingTh / 2 + effectiveGap + iconDrawHeight / 2);
          } else if (expansionDirection === 'top') {
            positionOverrides[linkedIconProperty] = { x: gx, y: gy };
            positionOverrides[pName] = offsetPos(0, -(iconDrawHeight / 2 + effectiveGap + spacingTh / 2));
          } else {
            positionOverrides[pName] = offsetPos(0, -(effectiveGap + iconDrawHeight) / 2);
            positionOverrides[linkedIconProperty] = offsetPos(0, (eth + effectiveGap) / 2);
          }
          break;
        }
        // "above" = icon sits ABOVE the text
        case 'above': {
          if (expansionDirection === 'top') {
            positionOverrides[pName] = { x: gx, y: gy };
            positionOverrides[linkedIconProperty] = offsetPos(0, -(spacingTh / 2 + effectiveGap + iconDrawHeight / 2));
          } else if (expansionDirection === 'bottom') {
            positionOverrides[linkedIconProperty] = { x: gx, y: gy };
            positionOverrides[pName] = offsetPos(0, iconDrawHeight / 2 + effectiveGap + spacingTh / 2);
          } else {
            positionOverrides[pName] = offsetPos(0, (effectiveGap + iconDrawHeight) / 2);
            positionOverrides[linkedIconProperty] = offsetPos(0, -(eth + effectiveGap) / 2);
          }
          break;
        }
        // "right" = icon sits to the RIGHT of the text
        case 'right': {
          let etw = (c.value || '').length * fontSize * 0.55;
          if (customizerCanvasCtx && c.value) {
            customizerCanvasCtx.save();
            customizerCanvasCtx.font = fontFamily ? fontSize + "px '" + fontFamily + "'" : fontSize + 'px sans-serif';
            etw = customizerCanvasCtx.measureText(c.value.split('\n')[0]).width;
            customizerCanvasCtx.restore();
          }
          if (expansionDirection === 'right') {
            positionOverrides[pName] = { x: gx, y: gy };
            positionOverrides[linkedIconProperty] = offsetPos(etw / 2 + effectiveGap + iconDrawWidth / 2, 0);
          } else if (expansionDirection === 'left') {
            positionOverrides[linkedIconProperty] = { x: gx, y: gy };
            positionOverrides[pName] = offsetPos(-(iconDrawWidth / 2 + effectiveGap + etw / 2), 0);
          } else {
            positionOverrides[pName] = offsetPos(-(effectiveGap + iconDrawWidth) / 2, 0);
            positionOverrides[linkedIconProperty] = offsetPos((etw + effectiveGap) / 2, 0);
          }
          break;
        }
        // "left" = icon sits to the LEFT of the text
        case 'left': {
          let etw = (c.value || '').length * fontSize * 0.55;
          if (customizerCanvasCtx && c.value) {
            customizerCanvasCtx.save();
            customizerCanvasCtx.font = fontFamily ? fontSize + "px '" + fontFamily + "'" : fontSize + 'px sans-serif';
            etw = customizerCanvasCtx.measureText(c.value.split('\n')[0]).width;
            customizerCanvasCtx.restore();
          }
          if (expansionDirection === 'left') {
            positionOverrides[pName] = { x: gx, y: gy };
            positionOverrides[linkedIconProperty] = offsetPos(-(etw / 2 + effectiveGap + iconDrawWidth / 2), 0);
          } else if (expansionDirection === 'right') {
            positionOverrides[linkedIconProperty] = { x: gx, y: gy };
            positionOverrides[pName] = offsetPos(iconDrawWidth / 2 + effectiveGap + etw / 2, 0);
          } else {
            positionOverrides[pName] = offsetPos((effectiveGap + iconDrawWidth) / 2, 0);
            positionOverrides[linkedIconProperty] = offsetPos(-(etw + effectiveGap) / 2, 0);
          }
          break;
        }
      }
    }

    // -----------------------------------------------------------------------
    // PHASE B: DRAW EACH CUSTOMIZATION
    //
    // Iterate every property in the customizations object.  Each one is
    // either an icon (type === 'icon') or text (type === 'text').
    // Font-type entries are metadata only and are consumed above/below
    // when styling text — they produce no direct canvas output.
    // -----------------------------------------------------------------------
  for (const propName in customizations) {
    const customization = customizations[propName];

    // Location filter: in primary mode draw all, otherwise only active location.
    // Keys are suffixed with location index: Text_0, Icon_1, etc.
    // Also allow legacy unsuffixed keys (single-location products).
    const locSuffix = propName.match(/_(\d+)$/);
    const locIdxFilter = locSuffix ? parseInt(locSuffix[1], 10) : 0;

    // Skip bare keys (e.g. "Text") when a location-keyed version exists (e.g. "Text_0")
    // to prevent drawing the same customization twice.
    if (!locSuffix && customizations[propName + '_0']) continue;

    if (locSuffix && !isPrimaryMode && locIdxFilter !== window.currentLocationIndex && !sharesCurrentImage(locIdxFilter)) continue;

    // In primary mode, get override coordinates for this location
    const drawLocIdx = locSuffix ? parseInt(locSuffix[1], 10) : 0;
    const primaryOvr = isPrimaryMode ? getPrimaryOverrides(drawLocIdx) : null;

    // --- ICON DRAWING ---
    if (customization.type === 'icon' && customization.url) {
        // Create a new Image for this icon.  crossOrigin is required so that
        // the canvas remains exportable via toDataURL() (thumbnail generation).
        const img = new Image();
        img.crossOrigin = 'anonymous';

        // Capture the redraw version at the time this image load starts.
        // If redrawCanvas() is called again before the image finishes loading,
        // imageLoadVersion will be stale and drawImage() will bail out.
        const imageLoadVersion = redrawVersion;

        // Inner helper: actually paint the icon onto the canvas.
        const drawImage = () => {
          // Stale-load guard (see REDRAW VERSION PATTERN above).
          if (imageLoadVersion !== redrawVersion) {
            return;
          }

          // Use the layout-group override position (already in px from Phase A),
          // otherwise convert the customization's own x/y from % to px.
          // In primary mode, use primary image coordinates.
          const x = positionOverrides[propName]?.x ?? (primaryOvr ? primaryOvr.x : pctToPxX(customization.x));
          const y = positionOverrides[propName]?.y ?? (primaryOvr ? primaryOvr.y : pctToPxY(customization.y));

          // Size priority: primary override > per-customization > global window.iconSize
          // (set by the resize handler below) > module-level currentIconSize.
          const size = (primaryOvr && primaryOvr.iconSize) ? primaryOvr.iconSize : (customization.size || window.iconSize || currentIconSize);
          const rotation = (primaryOvr && primaryOvr.rotation !== undefined) ? primaryOvr.rotation : (customization.rotation || 0);
          const borderColor = customization.borderColor || null;

          // save()/restore() isolates rotation transforms and clip state so
          // drawing one icon does not affect subsequent draws.
          customizerCanvasCtx.save();

          // Maintain the image's native aspect ratio within the square
          // bounding box defined by `size`.
          const aspectRatio = img.width / img.height;
          let drawWidth = size;
          let drawHeight = size;

          if (aspectRatio > 1) {
            drawHeight = size / aspectRatio; // landscape: shrink height
          } else {
            drawWidth = size * aspectRatio;  // portrait:  shrink width
          }

          // For handwriting uploads the store provides a `defaultSize`
          // representing a fixed guide box.  We vertically center the drawn
          // image within that box so short handwriting doesn't sit at the
          // top edge.  For regular icons centerOffset is 0 (no shift).
          const centerOffset = customization.defaultSize
            ? (customization.defaultSize - drawHeight) / 2
            : 0;

          // Rotation: translate the origin to (x,y) first, rotate, then draw
          // at negative half-extents so the image is centred on (x,y).
          // Without rotation we skip the translate/rotate and draw directly.
          if (rotation !== 0) {
            customizerCanvasCtx.translate(x, y);
            customizerCanvasCtx.rotate(rotation * Math.PI / 180);
            customizerCanvasCtx.drawImage(img, -drawWidth / 2, -drawHeight / 2 + centerOffset, drawWidth, drawHeight);
          } else {
            customizerCanvasCtx.drawImage(img, x - drawWidth / 2, y - drawHeight / 2 + centerOffset, drawWidth, drawHeight);
          }

          // -----------------------------------------------------------------
          // DESIGN MODE BOUNDING BOXES (Shopify Theme Editor only)
          //
          // When the theme is previewed inside the Shopify theme editor
          // (window.Shopify.designMode === true), we overlay coloured
          // bounding boxes around handwriting-upload icons to help the
          // merchant visualize the min/max/default size constraints:
          //   Red dashed   = minSize  (smallest the icon can scale to)
          //   Blue dashed  = maxSize  (largest the icon can scale to)
          //   Green solid  = defaultSize (initial / guide size)
          //
          // These boxes are purely a design-time aid and never appear in
          // the storefront for end customers.
          // -----------------------------------------------------------------
          if (window.Shopify && window.Shopify.designMode && customization.minSize !== undefined && customization.maxSize !== undefined && customization.defaultSize !== undefined) {
            const drawBoundingBox = (size, color, lineWidth, dashPattern) => {
              let boxHeight = size;
              let boxWidth = size * aspectRatio;

              customizerCanvasCtx.strokeStyle = color;
              customizerCanvasCtx.lineWidth = lineWidth;

              if (dashPattern) {
                customizerCanvasCtx.setLineDash(dashPattern);
              } else {
                customizerCanvasCtx.setLineDash([]);
              }

              // Draw box centered at x, y (accounting for rotation if applied)
              if (rotation !== 0) {
                // If rotated, boxes are already in rotated coordinate system
                customizerCanvasCtx.strokeRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
              } else {
                // No rotation, draw at absolute position
                customizerCanvasCtx.strokeRect(x - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight);
              }
            };

            // Draw min size box (red dashed) - only if scaling is enabled
            if (customization.enableSizeScaling !== false) {
              drawBoundingBox(customization.minSize, '#FF0000', 2, [5, 5]);
            }

            // Draw max size box (blue dashed) - only if scaling is enabled
            if (customization.enableSizeScaling !== false) {
              drawBoundingBox(customization.maxSize, '#0000FF', 2, [5, 5]);
            }

            // Draw default size box (green solid) - always show
            drawBoundingBox(customization.defaultSize, '#00FF00', 2, []);
          }

          customizerCanvasCtx.restore();
          // Schedule a debounced thumbnail update (icon has been painted).
          updateThumbnailDebounced();
        };

        // --- Image load callbacks ---
        img.onload = () => {
          // Cache the aspect ratio so future layout-group calculations know
          // the real proportions before the next image load completes.
          const wasAlreadyCached = iconAspectRatioCache.has(customization.url);
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            iconAspectRatioCache.set(customization.url, img.naturalWidth / img.naturalHeight);
          }
          drawImage();

          // If this is the first time we learned the real aspect ratio,
          // trigger a full redraw so that layout-group position overrides
          // are recalculated with accurate icon dimensions.
          if (!wasAlreadyCached && typeof window.redrawCanvas === 'function') {
            window.redrawCanvas();
          }
        };
        img.onerror = () => {
          console.error('Failed to load image:', customization.url);
        };

        // Kick off the load (may resolve synchronously if browser-cached).
        img.src = customization.url;

        // Fast path: if the image was already in the browser cache (or is a
        // data: URL), img.complete is true immediately after setting .src.
        // Draw without waiting for the onload event.
        if (img.complete && img.naturalWidth > 0) {
          if (!iconAspectRatioCache.has(customization.url) && img.naturalWidth > 0 && img.naturalHeight > 0) {
            iconAspectRatioCache.set(customization.url, img.naturalWidth / img.naturalHeight);
          }
          drawImage();
        }
      // --- TEXT DRAWING ---
      } else if (customization.type === 'text' && customization.value) {
        // Capture redraw version for the async font-load callback below.
        const textLoadVersion = redrawVersion;

        // Position: layout-group override (already px) takes priority,
        // otherwise convert from % to px. In primary mode, use primary coords.
        let textX = positionOverrides[propName]?.x ?? (primaryOvr ? primaryOvr.x : pctToPxX(customization.x));
        const textY = positionOverrides[propName]?.y ?? (primaryOvr ? primaryOvr.y : pctToPxY(customization.y));

        // Font size: primary override > per-customization > global > module-level.
        const baseFontSize = (primaryOvr && primaryOvr.fontSize) ? primaryOvr.fontSize : (customization.size || window.textSize || currentTextSize);
        const rotation = (primaryOvr && primaryOvr.rotation !== undefined) ? primaryOvr.rotation : (customization.rotation || 0);
        const multilineConfig = customization.multilineConfig;
        const alignment = customization.alignment || 'center';

        // Look up the font customization that styles this text property.
        // Convention: a font entry either has appliesToProperty pointing at
        // this propName, or its key is "font-<propName>".
        // fontScale multiplies the base size; fontLineHeight multiplies the
        // font size to get the pixel distance between lines.
        let fontScale = 1;
        let fontLineHeight = 1.1;
        let fontFamily = null;
        let fontUrl = null;
        // For multi-location keys like "Text_1", also match fonts that
        // apply to the base name "Text" (set by the initial DOM scan).
        const basePropName = propName.replace(/_\d+$/, '');
        for (const key in customizations) {
          const fontCustomization = customizations[key];
          if (fontCustomization.type === 'font' &&
              (fontCustomization.appliesToProperty === propName ||
               fontCustomization.appliesToProperty === basePropName ||
               (key.includes('font-') && key.replace('font-', '') === propName))) {
            fontScale = fontCustomization.fontScale !== null && fontCustomization.fontScale !== undefined
              ? fontCustomization.fontScale
              : 1;
            fontLineHeight = fontCustomization.lineHeight !== null && fontCustomization.lineHeight !== undefined
              ? fontCustomization.lineHeight
              : 1.1;
            fontFamily = fontCustomization.fontFamily || null;
            fontUrl = fontCustomization.fontUrl || null;
            console.log('[canvas-renderer] Font match for text "' + propName + '": key=' + key, 'fontFamily=' + fontFamily, 'fontUrl=' + fontUrl, 'fontScale=' + fontScale);
            break;
          }
        }
        if (!fontFamily) {
          console.log('[canvas-renderer] No fontFamily found for "' + propName + '", using CSS class fallback:', customization.font);
        }

        // Final effective font size after applying the per-font scale.
        const fontSize = baseFontSize * fontScale;

        // Text alignment: always center-aligned and center-justified for
        // combined layout groups. Legacy layout groups (no expansionDirection)
        // keep the old left/right hugging behavior.
        let textAlign = 'center';
        if (customization.layoutGroup && !customization.layoutGroup.expansionDirection) {
          const lgDir = customization.layoutGroup.direction;
          if (lgDir === 'right') {
            textAlign = 'right';
            const maxChars = multilineConfig?.maxCharsPerLine || 12;
            textX = textX + (maxChars * fontSize * 0.55) / 2;
          } else if (lgDir === 'left') {
            textAlign = 'left';
            const maxChars = multilineConfig?.maxCharsPerLine || 12;
            textX = textX - (maxChars * fontSize * 0.55) / 2;
          }
        }

        // --- Multi-line text handling ---
        // If multiline is enabled and the value contains a '\n', split into
        // two lines and compute the vertical offset to position them
        // according to the alignment setting (top / center / bottom).
        let line1 = customization.value;
        let line2 = '';
        let lineHeight = fontSize * fontLineHeight; // px distance between baselines
        let offsetY = 0; // vertical shift applied to each line from the anchor point

        if (multilineConfig && multilineConfig.enabled && customization.value.includes('\n')) {
          const lines = customization.value.split('\n');
          line1 = lines[0] || '';
          line2 = lines[1] || '';

          if (alignment === 'top') {
            // Anchor = top of first line; second line sits below.
            offsetY = 0;
          } else if (alignment === 'bottom') {
            // Anchor = bottom of second line; first line sits above.
            offsetY = lineHeight;
          } else {
            // Center (default): anchor = midpoint between the two lines.
            offsetY = lineHeight / 2;
          }
        }

        // --- Resolve the font for canvas ---
        // If we have a font file URL + family name (from metaobject font_file),
        // use the FontFace API directly. Otherwise fall back to the CSS class trick.
        let fontString;
        if (fontFamily) {
          // Font file approach: use the @font-face family name loaded in the DOM
          fontString = fontSize + 'px ' + "'" + fontFamily + "'";
        } else {
          // Legacy CSS class approach: create a hidden element to read computed font
          const tempElement = document.createElement('span');
          tempElement.className = customization.font;
          tempElement.style.fontSize = fontSize + 'px';
          tempElement.style.position = 'absolute';
          tempElement.style.visibility = 'hidden';
          tempElement.style.opacity = '0';
          tempElement.textContent = 'A';
          document.body.appendChild(tempElement);
          const computedStyle = window.getComputedStyle(tempElement);
          fontString = computedStyle.font;
          document.body.removeChild(tempElement);
        }

        // Text fill color — maintained reactively by the color-selector UI
        // and stored in customizerStore.customizations[propName].color.
        const fillColor = customization.color || '#000000';

        // Inner helper: paints the text (single or multi-line) onto the canvas.
        // Called twice per text customization: once synchronously (fallback font)
        // and once asynchronously after the web font is confirmed loaded.
        const drawTextContent = () => {
          customizerCanvasCtx.font = fontString;
          customizerCanvasCtx.fillStyle = fillColor;
          customizerCanvasCtx.textBaseline = 'middle';
          customizerCanvasCtx.textAlign = textAlign;

          // Apply rotation if specified
          if (rotation !== 0) {
            customizerCanvasCtx.translate(textX, textY);
            customizerCanvasCtx.rotate(rotation * Math.PI / 180);
            if (line2) {
              if (alignment === 'top') {
                customizerCanvasCtx.fillText(line1, 0, 0);
                customizerCanvasCtx.fillText(line2, 0, lineHeight);
              } else if (alignment === 'bottom') {
                customizerCanvasCtx.fillText(line1, 0, -lineHeight);
                customizerCanvasCtx.fillText(line2, 0, 0);
              } else {
                customizerCanvasCtx.fillText(line1, 0, -offsetY);
                customizerCanvasCtx.fillText(line2, 0, offsetY);
              }
            } else {
              customizerCanvasCtx.fillText(line1, 0, 0);
            }
          } else {
            if (line2) {
              if (alignment === 'top') {
                customizerCanvasCtx.fillText(line1, textX, textY);
                customizerCanvasCtx.fillText(line2, textX, textY + lineHeight);
              } else if (alignment === 'bottom') {
                customizerCanvasCtx.fillText(line1, textX, textY - lineHeight);
                customizerCanvasCtx.fillText(line2, textX, textY);
              } else {
                customizerCanvasCtx.fillText(line1, textX, textY - offsetY);
                customizerCanvasCtx.fillText(line2, textX, textY + offsetY);
              }
            } else {
              customizerCanvasCtx.fillText(line1, textX, textY);
            }
          }

          // Design-mode anchor dot: when editing the theme in Shopify's
          // theme editor, show a small red dot at the text's anchor point
          // so the merchant can see exactly where x/y places the text.
          if (window.Shopify && window.Shopify.designMode) {
            customizerCanvasCtx.save();
            customizerCanvasCtx.fillStyle = 'red';
            customizerCanvasCtx.beginPath();
            const dotX = rotation !== 0 ? 0 : textX;
            const dotY = rotation !== 0 ? 0 : textY;
            customizerCanvasCtx.arc(dotX, dotY, 3, 0, 2 * Math.PI);
            customizerCanvasCtx.fill();
            customizerCanvasCtx.restore();
          }
        };

        // -----------------------------------------------------------------
        // FONT LOADING: TWO-PASS DRAW STRATEGY
        //
        // Pass 1 (synchronous): draw immediately using whatever font the
        // browser has available right now. If the web font hasn't loaded
        // yet this renders with the CSS fallback font. This guarantees
        // the canvas always shows *something* — important because the
        // thumbnail updater may snapshot the canvas before the font arrives.
        //
        // Pass 2 (async): call document.fonts.load(fontString) which
        // resolves once the web font is ready (instantly if already cached).
        // Re-draw the text with the correct typeface.  The redrawVersion
        // guard (textLoadVersion) prevents stale redraws if the store
        // changed between Pass 1 and Pass 2.
        // -----------------------------------------------------------------
        customizerCanvasCtx.save();
        drawTextContent();
        customizerCanvasCtx.restore();

        if (document.fonts && document.fonts.load) {
          document.fonts.load(fontString).then(() => {
            // Stale-load guard: skip if a newer redraw superseded this one.
            if (textLoadVersion !== redrawVersion) return;
            customizerCanvasCtx.save();
            drawTextContent();
            customizerCanvasCtx.restore();
            updateThumbnailDebounced();
          }).catch(() => {
            // Font load failed — Pass 1 already rendered with the fallback.
          });
        }
      }
    }

    // -----------------------------------------------------------------------
    // DEBUG COLOUR SWATCH
    // If any icon customization specifies a borderColor (used for
    // handwriting-upload border previews), draw a small coloured square in
    // the top-right corner of the canvas so the merchant can see which
    // border colour is active.
    // -----------------------------------------------------------------------
    let debugBorderColor = null;
    for (const propName in customizations) {
      const customization = customizations[propName];
      if (customization.type === 'icon' && customization.borderColor) {
        debugBorderColor = customization.borderColor;
        break;
      }
    }

    if (debugBorderColor) {
      const boxSize = 50;
      const offset = 10;
      const x = canvasWidth - boxSize - offset;
      const y = offset;

      customizerCanvasCtx.save();
      customizerCanvasCtx.fillStyle = debugBorderColor;
      customizerCanvasCtx.fillRect(x, y, boxSize, boxSize);
      customizerCanvasCtx.strokeStyle = '#000000';
      customizerCanvasCtx.lineWidth = 1;
      customizerCanvasCtx.strokeRect(x, y, boxSize, boxSize);
      customizerCanvasCtx.restore();
    }

    // Kick off a debounced thumbnail update after all synchronous draws are
    // done.  Async icon/text callbacks also call this independently.
    updateThumbnailDebounced();
  } // end drawCustomizations()

  // -------------------------------------------------------------------------
  // THUMBNAIL UPDATE (debounced)
  //
  // After every canvas paint we copy the canvas contents into the product
  // gallery thumbnail so the customer sees a live preview of their
  // customizations in the image carousel.
  //
  // The update is debounced at 500ms to avoid excessive toDataURL() calls
  // during rapid store mutations (e.g. typing text).  Every caller
  // (synchronous draw + async icon/font callbacks) uses the debounced
  // wrapper; only the *last* call within the 500ms window actually fires.
  // -------------------------------------------------------------------------
  function updateThumbnail() {
    const thumbnailImg = document.querySelector('.thumbnails .preview-base img');
    if (thumbnailImg) {
      thumbnailImg.src = document.getElementById('customizer-canvas').toDataURL('image/png');
    }
  }

  let _thumbnailTimer = null;
  function updateThumbnailDebounced() {
    clearTimeout(_thumbnailTimer);
    _thumbnailTimer = setTimeout(updateThumbnail, 500);
  }

  // -------------------------------------------------------------------------
  // STORE SUBSCRIPTION – reactive canvas redraws
  //
  // Pinia's $subscribe fires on *every* mutation to the store (text input,
  // icon change, color pick, font switch, etc.).  We redraw the canvas
  // whenever the store changes, but with two guards:
  //
  //   1. The customizer UI must be open (.customiser-toggle.active) AND the
  //      canvas must not be hidden.  This prevents an unnecessary clear +
  //      redraw when the user simply toggles the customizer panel closed.
  //
  //   2. If _inputRedrawScheduled is set (by a debounced text-input handler
  //      elsewhere), we skip this redraw because the input handler already
  //      has a redraw queued at the end of its debounce window — running
  //      two would cause a visible flicker.
  // -------------------------------------------------------------------------
  customizerStore = null;
  if (typeof window.useCustomizerStore === 'function') {
    try {
      customizerStore = window.useCustomizerStore();
    } catch (e) {
      console.error('canvas-renderer.js: Failed to create store:', e);
    }
  }
  if (customizerStore && typeof customizerStore.$subscribe === 'function') {
    customizerStore.$subscribe(() => {
      const customizerActive = document.querySelector('.customiser-toggle.active');
      const canvasVisible = document.getElementById('customizer-canvas') &&
                           !document.getElementById('customizer-canvas').classList.contains('hidden');

      if (customizerActive && canvasVisible && typeof window.redrawCanvas === 'function') {
        if (typeof _inputRedrawScheduled !== 'undefined' && _inputRedrawScheduled) return;
        window.redrawCanvas();
      }
    });
  }

  // -------------------------------------------------------------------------
  // ZOOM TOGGLE
  // Toggles the .zoomed CSS class on the canvas, which (via CSS) scales the
  // canvas up with a transform and uses the focal-point transform-origin set
  // earlier to keep the product centred.  The button icon swaps between a
  // "zoom in" (+) and "zoom out" (-) magnifying glass SVG.
  // -------------------------------------------------------------------------
  const zoomToggle = document.querySelector('.zoom-toggle');
  if (zoomToggle) zoomToggle.addEventListener('click', function () {
    customizerCanvas.classList.toggle('zoomed');

    if (customizerCanvas.classList.contains('zoomed')) {
      // Show "zoom out" icon (minus magnifying glass)
      zoomToggle.innerHTML = `
     <svg class="w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM13.5 10.5h-6" />
</svg>
      `;
    } else {
      // Show "zoom in" icon (plus magnifying glass)
      zoomToggle.innerHTML = `
       <svg class="w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-8">
      <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607ZM10.5 7.5v6m3-3h-6" />
    </svg>
      `;
    }
  });
}); // end DOMContentLoaded

// =============================================================================
// GLOBAL ICON / TEXT SIZE CALCULATION + MOBILE SCALING
//
// This section runs at module-load time (outside DOMContentLoaded) because
// the size values are needed by both the canvas renderer and the customizer
// UI components.
//
// Data source: #canvas-settings is a hidden DOM element whose data attributes
// (data-icon-size, data-text-size) are populated by the Shopify Liquid template
// from the theme block settings.  If the element is missing, hard-coded
// defaults are used.
//
// Mobile scaling: on viewports <= 767px, icons are scaled 30% larger and text
// 20% larger for better visibility and touch-friendliness.  The resize handler
// recalculates on every breakpoint crossing and triggers a canvas redraw if
// the sizes actually changed.
// =============================================================================

const settingsEl = document.getElementById('canvas-settings');
const baseIconSize = settingsEl ? parseInt(settingsEl.dataset.iconSize, 10) : 50;
const baseTextSize = settingsEl ? parseInt(settingsEl.dataset.textSize, 10) : 32;

// Apply mobile multipliers on initial load.
const isMobile = window.innerWidth <= 767;
const iconSize = isMobile ? Math.round(baseIconSize * 1.3) : baseIconSize;
const textSize = isMobile ? Math.round(baseTextSize * 1.2) : baseTextSize;

// Module-level tracking variables — updated by the resize handler below.
let currentIconSize = iconSize;
let currentTextSize = textSize;

// Resize handler: recalculate sizes when the viewport crosses the mobile
// breakpoint (767px).  Avoids unnecessary redraws by comparing old vs new.
window.addEventListener('resize', function() {
  const newIsMobile = window.innerWidth <= 767;
  const newIconSize = newIsMobile ? Math.round(baseIconSize * 1.3) : baseIconSize;
  const newTextSize = newIsMobile ? Math.round(baseTextSize * 1.2) : baseTextSize;

  if (newIconSize !== currentIconSize || newTextSize !== currentTextSize) {
    currentIconSize = newIconSize;
    currentTextSize = newTextSize;

    // Publish to window so other modules (and the drawCustomizations loop
    // inside the DOMContentLoaded closure) pick up the new values.
    window.iconSize = currentIconSize;
    window.textSize = currentTextSize;

    // Only redraw if the customizer is open and the canvas is visible.
    const customizerActive = document.querySelector('.customiser-toggle.active');
    const canvasVisible = document.getElementById('customizer-canvas') &&
                         !document.getElementById('customizer-canvas').classList.contains('hidden');

    if (typeof window.redrawCanvas === 'function' && customizerActive && canvasVisible) {
      window.redrawCanvas();
    }
  }
});

// Publish initial sizes to window globals so they are available immediately
// to any module that reads window.iconSize / window.textSize.
window.iconSize = currentIconSize;
window.textSize = currentTextSize;
